import { Col, Flex, Form, Row } from 'antd';
import { AutoCompleteCommon } from 'components/common';
import BarcodeCommon from 'components/common/Barcode/BarcodeCommon';
import { ButtonCommon } from 'components/common/Button/ButtonCommon';
import CollapseCommon from 'components/common/Collapse/CollapseCommon';
import { InputCommon } from 'components/common/Input/InputCommon';
import PrefixedInput from 'components/common/Input/PrefixedInput';
import LabelWithRequired from 'components/common/Label/LabelWithRequired';
import SelectCommon from 'components/common/Select/SelectCommon';
import { Brand } from 'models';
import React, { useEffect, useState } from 'react';
import { ProductInfoCollapseProps } from 'types/ProductType';
import { categoryRules, productNameRules } from 'utils/formRules';

const ProductInfoSection: React.FC<ProductInfoCollapseProps> = ({
  form,
  isEdit,
  isDetail = false,
  isInactive,
  categories,
  suppliers,
  productSuppliers,
  setProductSuppliers,
  filteredBrands,
  isLoadingCategories,
  isLoadingBrands,
  barcode,
  handleBarcode,
  handleCreatecode,
  handleCategoryChange,
  prefixCode,
  units,
  initialSupplierId,
  getBrandsByCategoryIds,
}) => {
  const [productDimensions, setProductDimensions] = useState<{ label: string; value: string }[]>([]);
  const [sizeSpecs, setSizeSpecs] = useState<{ label: string; value: string }[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [filteredSuppliers, setSuppliers] = useState<{ label: string; value: string }[]>([]);
  const [initBrands, setInitBrands] = useState<Brand[]>([]);
  const unitOptions =
    units
      ?.filter((unit) => unit.type === 'unit')
      .map((unit) => ({
        label: unit.name,
        value: unit.id,
      })) || [];
  const currencyUnits =
    units
      ?.filter((unit) => unit.type === 'currency')
      .map((unit) => ({
        label: unit.name,
        value: unit.id,
      })) || [];
  useEffect(() => {
    if (isDetail) {
      const initialCategoryId = form.getFieldValue('category');
      const initialBrandId = form.getFieldValue('brand');

      setSelectedCategoryId(initialCategoryId || null);
      setSelectedBrandId(initialBrandId || null);
    }
  }, [isDetail]);

  useEffect(() => {
    // Set default currencyUnitId to VNĐ if not set
    const currentValue = form.getFieldValue('currencyUnitId');
    const vndValue = currencyUnits.find((c) => c.label === 'VNĐ')?.value;
    if (!currentValue && vndValue) {
      form.setFieldsValue({ currencyUnitId: vndValue });
    }
  }, [form, currencyUnits]);

  useEffect(() => {
    if (suppliers) {
      const filteredOptions =
        suppliers
          ?.filter((supplier) => supplier.baseUser.name && supplier.id)
          .map((supplier) => ({
            label: supplier.baseUser.name as string,
            value: supplier.id as string,
          })) || [];
      setSuppliers(filteredOptions);

      if (initialSupplierId) {
        const initialSupplier = suppliers.find((supplier) => supplier.id === initialSupplierId);
        // console.log('initialSupplier', initialSupplier);
        if (initialSupplier) {
          form.setFieldsValue({ supplier: [initialSupplierId] });
          setProductSuppliers?.([initialSupplierId]);
        }
      }
    }
  }, [suppliers, initialSupplierId, isEdit]);
  useEffect(() => {
    if (selectedCategoryId) {
      const selectedCategory = categories.find((cat) => cat.id === selectedCategoryId);

      if (selectedCategory) {
        if (selectedCategory.productDimensions && selectedCategory.productDimensions.length > 0) {
          const dimensions = selectedCategory.productDimensions
            .filter((dim) => dim.isActive)
            .map((dim) => ({
              label: dim.name,
              value: dim.name,
            }));
          setProductDimensions(dimensions);
        } else {
          setProductDimensions([]);
        }
        if (selectedCategory.sizeSpecs && selectedCategory.sizeSpecs.length > 0) {
          const specs = selectedCategory.sizeSpecs
            .filter((spec) => spec.isActive)
            .map((spec) => ({
              label: spec.name,
              value: spec.name,
            }));
          setSizeSpecs(specs);
        } else {
          setSizeSpecs([]);
        }
      }
      if (selectedCategoryId) {
        const brandData = getBrandsByCategoryIds?.(selectedCategoryId);
        setInitBrands(brandData || []);
      }
    } else {
      setProductDimensions([]);
      setSizeSpecs([]);
    }
  }, [selectedCategoryId, categories, isDetail]);
  return (
    <CollapseCommon size='md' collapseKey='1' title='Thông tin sản phẩm'>
      <Form form={form} layout='vertical'>
        <Flex vertical gap={12}>
          <Flex justify='space-between' align='center' gap={24}>
            <Form.Item
              label={<LabelWithRequired label='Tên' />}
              required={false}
              name='name'
              rules={productNameRules}
              className='mb-0 w-full'
            >
              <InputCommon
                variantStatus='underline'
                type='label'
                disabled={!isEdit || isInactive}
                placeholder='Nhập vào tên sản phẩm'
                maxLength={100}
              />
            </Form.Item>
            <Form.Item label={<LabelWithRequired label='Barcode' />} name='barcode' className='mb-0'>
              <BarcodeCommon
                valueBarcode={barcode || form.getFieldValue('barcode')}
                onGenerate={handleBarcode}
                isDisabled={!isEdit || isDetail || isInactive}
              />
            </Form.Item>
          </Flex>

          <Row gutter={[24, 12]}>
            <Col xl={6} lg={8} md={12} sm={12} xs={24}>
              <Form.Item label={<LabelWithRequired label='Mã' />} name='code' className='mb-0'>
                <PrefixedInput
                  prefix={prefixCode || ''}
                  placeholder='Nhập vào mã...'
                  disabled={!isEdit || isDetail || isInactive}
                  inputProps={{
                    suffix: (
                      <ButtonCommon
                        color='barcode'
                        fullWidth
                        radius='rounded'
                        size='xs'
                        onClick={handleCreatecode}
                        isDisabled={!isEdit || isInactive || isDetail}
                        className={`${!isEdit || isInactive ? 'pointer-events-none' : ''}`}
                      >
                        Generate
                      </ButtonCommon>
                    ),
                    // className: 'xl:!h-[36px] !text-[14px] ',
                  }}
                />
              </Form.Item>
            </Col>
            <Col xl={6} lg={8} md={12} sm={12} xs={24}>
              <Form.Item
                label='Nhà cung cấp'
                required={false}
                // rules={categoryRules}
                name='supplier'
                className='mb-0'
              >
                <SelectCommon
                  // disabled={!isEdit || isInactive}
                  statusSelect={'default'}
                  placeholderSelect='Chọn nhà cung cấp'
                  options={
                    !isEdit && productSuppliers && productSuppliers?.length > 0
                      ? filteredSuppliers
                          ?.filter((supplier) => productSuppliers?.includes(supplier.value as string))
                          ?.map((supplier) => ({
                            label: supplier.label as string,
                            value: supplier.value as string,
                            disabled: !isEdit || isInactive,
                          })) || []
                      : filteredSuppliers?.map((supplier) => ({
                          label: supplier.label as string,
                          value: supplier.value as string,
                          disabled: !isEdit || isInactive,
                        })) || []
                  }
                  mode='multiple'
                  className='!text-[14px] multi-select-custom'
                  loading={isLoadingCategories}
                  allowClear={!(!isEdit || isInactive)}
                  maxTagCount={0}
                  maxTagPlaceholder={(e) => {
                    return `${e?.length} nhà cung cấp`;
                  }}
                  showSearch={!(!isEdit || isInactive)}
                  filterOption={false}
                  value={productSuppliers}
                  onChange={(values: string[]) => {
                    setProductSuppliers?.(values);
                    form.setFieldsValue({ supplier: values });
                  }}
                  onSearch={(value: string) => {
                    const filteredOptions =
                      suppliers
                        ?.filter((supplier) =>
                          (supplier.baseUser.name as string).toLowerCase().includes(value.toLowerCase()),
                        )
                        .map((supplier) => ({
                          label: supplier.baseUser.name as string,
                          value: supplier.id as string,
                        })) || [];
                    setSuppliers(filteredOptions);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xl={6} lg={8} md={12} sm={12} xs={24}>
              <Form.Item
                label={<span className='font-medium text-black'>Thương hiệu</span>}
                name='brand'
                className='mb-0'
                required={false}
              >
                <SelectCommon
                  disabled={!isEdit || isInactive || (!selectedCategoryId && !isDetail)}
                  statusSelect={!isEdit || isInactive || (!selectedCategoryId && !isDetail) ? 'disabled' : 'default'}
                  placeholderSelect={selectedCategoryId ? 'Chọn thương hiệu' : 'Vui lòng chọn danh mục trước'}
                  value={selectedBrandId}
                  options={
                    filteredBrands.length > 0
                      ? filteredBrands?.map((brand) => ({
                          label: brand.name,
                          value: brand.id,
                        }))
                      : initBrands?.map((brand) => ({
                          label: brand.name,
                          value: brand.id,
                        }))
                  }
                  allowClear
                  selectType='filterSelect'
                  loading={isLoadingBrands}
                  onChange={(value: string | null) => {
                    setSelectedBrandId(value);
                  }}
                />
              </Form.Item>
            </Col>
            <Col xl={6} lg={8} md={12} sm={12} xs={24}>
              <Form.Item
                label={<LabelWithRequired label='Danh mục' />}
                required={false}
                rules={categoryRules}
                name='category'
                className='mb-0'
              >
                <SelectCommon
                  disabled={!isEdit || isInactive}
                  statusSelect={isEdit ? 'default' : 'disabled'}
                  placeholderSelect='Chọn danh mục'
                  value={selectedCategoryId}
                  options={categories
                    .filter((category) => category && category.name && category.id && category.isActive)
                    .map((category) => ({
                      label: category.name,
                      value: category.id,
                    }))}
                  selectType='filterSelect'
                  loading={isLoadingCategories}
                  onChange={(value: string | null) => {
                    setSelectedCategoryId(value);
                    setSelectedBrandId(null);
                    form.setFieldsValue({
                      brand: null,
                    });
                    handleCategoryChange(value);
                  }}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xl={6} lg={8} md={12} sm={12} xs={24}>
              <Form.Item
                label={<span className='font-medium text-black'>Quy cách sản phẩm</span>}
                required={false}
                rules={[
                  {
                    max: 100,
                    message: 'Quy cách sản phẩm không được vượt quá 100 ký tự',
                  },
                ]}
                name='dimension'
                className='mb-0'
              >
                <AutoCompleteCommon
                  disabled={!isEdit || isInactive}
                  statusSelect={!isEdit || isInactive ? 'disabled' : 'default'}
                  sizeSelect='sm'
                  placeholderSelect={'Chọn quy cách'}
                  options={productDimensions}
                  widthSelect='100%'
                />
              </Form.Item>
            </Col>

            <Col xl={6} lg={8} md={12} sm={12} xs={24}>
              <Form.Item
                label='Độ dày'
                required={false}
                rules={[
                  {
                    max: 100,
                    message: 'Độ dày không được vượt quá 100 ký tự',
                  },
                ]}
                name='sizeSpec'
                className='mb-0'
              >
                <AutoCompleteCommon
                  disabled={!isEdit || isInactive}
                  statusSelect={!isEdit || isInactive ? 'disabled' : 'default'}
                  sizeSelect='sm'
                  placeholderSelect={'Chọn độ dày'}
                  options={sizeSpecs}
                  widthSelect='100%'
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8} xl={6}>
              <Form.Item
                label={<span className='font-medium text-black'>Màu sắc</span>}
                required={false}
                rules={[
                  {
                    max: 100,
                    message: 'Màu sắc không được vượt quá 100 ký tự',
                  },
                ]}
                name='color'
                className='mb-0'
              >
                <AutoCompleteCommon
                  placeholderSelect='Chọn màu sắc'
                  options={[
                    { label: 'Đỏ', value: 'Đỏ' },
                    { label: 'Vàng', value: 'Vàng' },
                    { label: 'Xanh', value: 'Xanh' },
                  ]}
                  disabled={!isEdit || isInactive}
                  statusSelect={!isEdit || isInactive ? 'disabled' : 'default'}
                  sizeSelect='sm'
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8} xl={6}>
              <Form.Item label='Số lượng' required={false} name='quantity' className='mb-0 w-full'>
                <InputCommon disabled type='money' placeholder='Nhập số lượng' />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8} xl={6}>
              <Form.Item
                label={<LabelWithRequired label='Giá nhập' />}
                required={false}
                rules={[
                  {
                    required: true,
                    message: 'Giá nhập là bắt buộc, bạn phải nhập giá trị!',
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const str = value.toString();
                      const [integerPart] = str.split('.');
                      const raw = integerPart.replace(/\D/g, '');
                      if (raw.length > 9) {
                        return Promise.reject('Giá nhập không được vượt quá 9 chữ số');
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
                name='costPrice'
                className='mb-0 w-full'
              >
                <InputCommon type='money' disabled={!isEdit || isInactive} placeholder='Nhập giá nhập vào' />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8} xl={6}>
              <Form.Item
                label={<LabelWithRequired label='Đơn vị bán mặc định' />}
                required={false}
                rules={categoryRules}
                name='baseUnitId'
                className='mb-0'
              >
                <SelectCommon
                  disabled={!isEdit || isInactive}
                  statusSelect={isEdit ? 'default' : 'disabled'}
                  placeholderSelect='Chọn đơn vị'
                  options={unitOptions}
                  selectType='filterSelect'
                  loading={isLoadingCategories}
                  allowClear
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12} lg={8} xl={6}>
              <Form.Item
                label={<LabelWithRequired label='Giá bán ra' />}
                required={false}
                rules={[
                  {
                    required: true,
                    message: 'Giá bán ra là bắt buộc, bạn phải nhập giá trị!',
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const str = value.toString();
                      const [integerPart] = str.split('.');
                      const raw = integerPart.replace(/\D/g, '');
                      if (raw.length > 9) {
                        return Promise.reject('Giá bán không được vượt quá 9 chữ số');
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                name='salePrice'
                className='mb-0 w-full'
              >
                <InputCommon type='money' disabled={!isEdit || isInactive} placeholder='Nhập giá bán ra' />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8} xl={6}>
              <Form.Item
                label={<LabelWithRequired label='Đơn vị tiền tệ' />}
                required={false}
                rules={categoryRules}
                name='currencyUnitId'
                className='mb-0'
                initialValue={currencyUnits.find((c) => c.label === 'VNĐ')?.value}
              >
                <SelectCommon
                  disabled={!isEdit || isInactive}
                  statusSelect={isEdit ? 'default' : 'disabled'}
                  placeholderSelect='Chọn đơn vị'
                  options={currencyUnits}
                  selectType='filterSelect'
                  loading={isLoadingCategories}
                  allowClear
                  value={form.getFieldValue('currencyUnitId') ?? currencyUnits.find((c) => c.label === 'VNĐ')?.value}
                  onChange={(value) => form.setFieldsValue({ currencyUnitId: value })}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12} lg={8} xl={6}>
              <Form.Item
                label={<span className='font-medium text-black'>Tỷ trọng</span>}
                required={false}
                name='density'
                className='mb-0 w-full'
              >
                <InputCommon
                  disabled={!isEdit || isInactive}
                  min={0}
                  max={100000}
                  type='decimal'
                  placeholder='Nhập tỷ trọng sản phẩm'
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={<span className='font-medium text-black'>Mô tả sản phẩm</span>}
            required={false}
            name='describeProduct'
            className='mb-0 '
            rules={[
              {
                max: 1000,
                message: 'Mô tả sản phẩm không được vượt quá 1000 ký tự',
              },
            ]}
          >
            <InputCommon
              maxLength={1000}
              disabled={!isEdit || isInactive}
              type='textarea'
              placeholder='Nhập mô tả sản phẩm...'
            />
          </Form.Item>
          <Form.Item name='productImages' hidden />
        </Flex>
      </Form>
    </CollapseCommon>
  );
};

export default ProductInfoSection;
